import {memo} from 'react';

import styled from 'styled-components';

import {media, flipOnLTR} from '~/shared/theme/utils';
import {generatePriceLabel} from '~/shared/components/PriceLabels';
import {body16Bold} from '~/shared/theme/typography';
import {SkeletonLoader} from '~/shared/components/Loaders';
import {SkeletonButton} from '~/shared/components/Loaders/components';
import PieButton from '~/shared/theme/pie/PieButton';

import ApplePayButton, {ApplePayButtonVariant} from './ApplePayButton';
import GooglePayButton from './GooglePayButton';

const SubmitButton = styled(PieButton).attrs({
  'data-test-id': 'checkoutSubmitOrderBtn',
  variant: 'primaryXL',
})<{centerSubmitBtn?: boolean}>`
  position: relative;
  width: calc(100% - 32px);
  height: 44px;
  ${body16Bold}
  text-align: center;
  cursor: pointer;
  margin-bottom: 8px;
  ${media.minLargeTablet`
    margin-bottom: 24px;
  `}

  &:disabled {
    background-color: ${({theme}) => theme.colors.gray500};
    cursor: not-allowed;
  }

  margin-right: auto;
  margin-left: auto;
  ${media.minLargeTablet`
    height: 48px;
    width: 100%;
    margin-right: inherit;
    margin-left: inherit;
    margin-bottom: 0px;
    ${flipOnLTR`
      margin-right:  ${({centerSubmitBtn}) => centerSubmitBtn && '154px'};
      margin-left: ${({centerSubmitBtn}) => centerSubmitBtn && '24px'};
    `}
  `}
`;

type Props = {
  onCheckoutSubmit: () => void;
  label: string;
  isDisabled: boolean;
  centerSubmitBtn?: boolean;
  totalToCharge: number;
  isLoading: boolean;
  isSubmitting: boolean;
  onApplePayButtonClick?: () => Promise<void>;
  onGooglePayButtonClick?: () => Promise<void>;
  paymentRequest?: google.payments.api.PaymentDataRequest;
};

const CheckoutSubmit = ({
  onCheckoutSubmit,
  label: translatedLabel,
  isDisabled,
  centerSubmitBtn,
  totalToCharge,
  isLoading,
  isSubmitting,
  onApplePayButtonClick,
  onGooglePayButtonClick,
  paymentRequest,
}: Props) => {
  if (onGooglePayButtonClick && paymentRequest) {
    return <GooglePayButton paymentRequest={paymentRequest} onLoadPaymentData={onGooglePayButtonClick}/>;
  }

  if (onApplePayButtonClick) {
    return <ApplePayButton variant={ApplePayButtonVariant.BLACK} onClick={onApplePayButtonClick}/>;
  }

  return (
    <SkeletonLoader shouldShowLoader={isLoading} LoaderComponent={SkeletonButton}>
      <SubmitButton isLoading={isDisabled && isSubmitting} centerSubmitBtn={centerSubmitBtn} onClick={onCheckoutSubmit}>
        {translatedLabel} ({generatePriceLabel({price: totalToCharge, format: true})})
      </SubmitButton>
    </SkeletonLoader>
  );
};

export default memo(CheckoutSubmit);
